import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import settingsService from '../../../services/settingsService';
import NotificationToast from '../../../components/ui/NotificationToast';

const TelecallersMaster = () => {
  const [telecallers, setTelecallers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    department: 'Sales',
    password: ''
  });

  useEffect(() => {
    loadTelecallers();
  }, []);

  const loadTelecallers = async () => {
    try {
      setLoading(true);
      const result = await settingsService.getTelecallers();
      if (result?.success) {
        setTelecallers(result.data || []);
      }
    } catch (error) {
      console.log('Error loading telecallers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEdit = (telecaller) => {
    setEditingId(telecaller.id);
    setFormData({
      email: telecaller.email || '',
      full_name: telecaller.full_name || '',
      phone: telecaller.phone || '',
      department: telecaller.department || 'Sales',
      password: '' // Don't pre-fill password
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      email: '',
      full_name: '',
      phone: '',
      department: 'Sales',
      password: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = editingId
        ? await settingsService.updateTelecaller(editingId, formData)
        : await settingsService.createTelecaller(formData);

      if (result?.success) {
        setNotification({ 
          message: `Lead Caller ${editingId ? 'updated' : 'created'} successfully`, 
          type: 'success' 
        });
        handleCancel();
        loadTelecallers();
      } else {
        setNotification({ 
          message: result?.error || `Failed to ${editingId ? 'update' : 'create'} lead caller`, 
          type: 'error' 
        });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
      console.log('Error saving telecaller:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead caller?')) {
      return;
    }

    try {
      const result = await settingsService.deleteTelecaller(id);
      if (result?.success) {
        setNotification({ message: 'Lead Caller deleted successfully', type: 'success' });
        loadTelecallers();
      } else {
        setNotification({ message: result?.error || 'Failed to delete lead caller', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
      console.log('Error deleting telecaller:', error);
    }
  };

  return (
    <div>
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-lg font-semibold text-foreground">Lead Callers</h4>
          <p className="text-sm text-muted-foreground">Manage lead caller users who can be assigned leads</p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
            <Icon name="Plus" size={16} />
            Add Lead Caller
          </Button>
        )}
      </div>

      {showForm && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <h5 className="font-semibold text-foreground mb-4">
            {editingId ? 'Edit Lead Caller' : 'Add New Lead Caller'}
          </h5>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Full Name *
                </label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="telecaller@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone
                </label>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+91-9876543210"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Department
                </label>
                <Input
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  placeholder="Sales"
                />
              </div>
              {!editingId && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Password *
                  </label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter password"
                    required={!editingId}
                  />
                </div>
              )}
              {editingId && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    New Password (leave blank to keep current)
                  </label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="Enter new password"
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" className="flex items-center gap-2">
                <Icon name="Save" size={16} />
                {editingId ? 'Update' : 'Create'}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading lead callers...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase">Department</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {telecallers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-muted-foreground">
                    No lead callers found. Click "Add Lead Caller" to create one.
                  </td>
                </tr>
              ) : (
                telecallers.map(telecaller => (
                  <tr key={telecaller.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm text-foreground">{telecaller.full_name}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{telecaller.email}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{telecaller.phone || '-'}</td>
                    <td className="px-4 py-3 text-sm text-foreground">{telecaller.department || '-'}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(telecaller)}
                          className="h-8 w-8 p-0"
                          title="Edit"
                        >
                          <Icon name="Edit" size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(telecaller.id)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TelecallersMaster;

